/**
 * Card asset resolver. Maps `(rank, suit)` to the public URL for the
 * face-up card image served out of `assets/cards/` (single source-of-
 * truth per 사용자 directive 2026-05-16). The `card-assets.ts` build
 * plugin copies `assets/cards/*.png` (PNG only — the renderer never
 * references a card `.svg`) into `arkhe-holdem-web/dist/cards/` at
 * build time + serves them at `/cards/...` under the dev server.
 *
 * Asset naming convention (verified against `assets/cards/` 2026-05-
 * 16): `<RANK><SUIT>.png`, where:
 *
 *   RANK: '0'='10' (10 spelled as zero on disk), '2'..'9', 'J', 'Q',
 *         'K', 'A'.
 *   SUIT: 'C' (clubs), 'D' (diamonds), 'H' (hearts), 'S' (spades).
 *
 * Card back is `back.png` (PNG only — no SVG version ships).
 *
 * **Front-face format = PNG** (intentional, not SVG). The shipped
 * SVG assets declare `width="744" height="1052"` with no `viewBox`
 * AND position their visible content via large `transform=translate(...)`
 * offsets, so a browser-rendered `<img>` only paints the content
 * inside a small fraction of the declared canvas — every face-up
 * pip + rank glyph collapses to roughly 1/9 of the card frame.
 * The PNG variants are tightly cropped (226x314, content fills the
 * entire raster) so they downscale crisply into the 64x90 / 96x134
 * card frame the default skin reserves. A future regeneration of
 * the SVG assets with an explicit `viewBox` would let us swap back.
 */

/** Rank 2..=14 (J=11 Q=12 K=13 A=14) → asset prefix char. */
export function rankChar(rank: number): string | null {
  if (!Number.isInteger(rank)) return null;
  if (rank >= 2 && rank <= 9) return String(rank);
  if (rank === 10) return "0";
  if (rank === 11) return "J";
  if (rank === 12) return "Q";
  if (rank === 13) return "K";
  if (rank === 14) return "A";
  return null;
}

/** Lowercase suit on the wire → asset suffix char. */
export function suitChar(suit: "c" | "d" | "h" | "s"): string {
  return suit.toUpperCase();
}

/**
 * Public URL for the face-up card image (PNG — see module doc on
 * the SVG `viewBox` gap). Returns `null` for an out-of-range rank
 * so the caller can fall back to the face-down image (or surface
 * "??" placeholder text).
 */
export function cardFrontUrl(
  rank: number,
  suit: "c" | "d" | "h" | "s",
): string | null {
  const r = rankChar(rank);
  if (r === null) return null;
  return `/cards/${r}${suitChar(suit)}.png`;
}

/** Face-down card asset (PNG; no SVG version ships in
 *  `assets/cards/`). */
export const CARD_BACK_URL = "/cards/back.png";

/** Pretty-name a card for `aria-label` / screen-reader output. */
export function cardAriaLabel(
  rank: number,
  suit: "c" | "d" | "h" | "s",
): string {
  const rankName = (() => {
    if (rank === 11) return "Jack";
    if (rank === 12) return "Queen";
    if (rank === 13) return "King";
    if (rank === 14) return "Ace";
    return String(rank);
  })();
  const suitName = { c: "Clubs", d: "Diamonds", h: "Hearts", s: "Spades" }[
    suit
  ];
  return `${rankName} of ${suitName}`;
}

/**
 * Deck-byte index → (rank, suit). Mirrors
 * `arkhe_holdem_primitives::card::Card::from_byte` which builds the
 * canonical deck by nested `rank * 4 + suit` loops (rank 2..=14,
 * suit C=0 / D=1 / H=2 / S=3). The arkhe-rand TS port emits 0..51
 * indices for `my_hole_cards`, and this helper turns those into
 * the `(rank, suit)` pair the renderer needs.
 */
export function cardIndexToRankSuit(idx: number): {
  rank: number;
  suit: "c" | "d" | "h" | "s";
} | null {
  if (!Number.isInteger(idx)) return null;
  if (idx < 0 || idx > 51) return null;
  const rank = Math.floor(idx / 4) + 2;
  const suitIdx = idx % 4;
  const suit = (["c", "d", "h", "s"] as const)[suitIdx];
  if (suit === undefined) return null;
  return { rank, suit };
}

/**
 * (rank, suit) → deck-byte index. The exact inverse of
 * `cardIndexToRankSuit`, and the reason it exists: since wire minor 47
 * the server sends the caller's own hole cards as `{rank, suit}` pairs
 * (the representation `board` and the showdown reveal already use),
 * while every renderer downstream still speaks the 0..51 deck index.
 * Converting once at the wire boundary keeps that one representation
 * change from spreading through the seat slot signature.
 *
 * Returns `null` for anything outside the canonical deck so a
 * malformed frame surfaces as "no cards" rather than a wrong card.
 */
export function rankSuitToCardIndex(rank: number, suit: string): number | null {
  if (!Number.isInteger(rank) || rank < 2 || rank > 14) return null;
  const suitIdx = ["c", "d", "h", "s"].indexOf(suit);
  if (suitIdx < 0) return null;
  return (rank - 2) * 4 + suitIdx;
}
